import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Building,
  FileText,
  PenToolIcon as Tool,
  CreditCard,
  Plus,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Trash2,
  Eye,
} from "lucide-react";
import { useAuth } from "@/components/auth-provider";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Import services and components
import {
  getTenantDashboardData,
  getDaysUntilNextPayment,
} from "@/lib/tenant-service";
import { MaintenanceRequestDialog } from "@/components/maintenance/maintenance-request-dialog";
import { LeaseDetailsCard } from "@/components/leases/lease-details-card";
import { LeaseSigningDialog } from "@/components/leases/lease-signing-dialog";
import {
  PaymentHistoryTable,
  Payment,
} from "@/components/payments/payment-history-table";
import { DocumentList, Document } from "@/components/documents/document-list";
import { LeaseAgreement, Property, MaintenanceRequest } from "@/types/types";
import { formatNaira } from "@/lib/utils";
import { LeaseStatusBadge } from "@/lib/lease-utils";
import {
  getTenantActions,
  MaintenanceStatusBadge,
} from "@/lib/maintenance-utils";
import {
  updateMaintenanceStatus,
  deleteMaintenanceRequest,
  subscribeToMaintenanceRequests,
} from "@/lib/maintenance-service";
import { MaintenanceDetailsDialog } from "@/components/maintenance/maintenance-details-dialog";
import { DashboardHeader } from "@/components/dashboard/dashboard-header";

export default function TenantDashboard() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // State for data
  const [lease, setLease] = useState<LeaseAgreement | null>(null);
  const [property, setProperty] = useState<Property | null>(null);
  const [maintenanceRequests, setMaintenanceRequests] = useState<
    MaintenanceRequest[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRequests: 0,
    pendingRequests: 0,
    completedRequests: 0,
  });

  // State for dialogs
  const [isMaintenanceDialogOpen, setIsMaintenanceDialogOpen] = useState(false);
  const [isLeaseSigningDialogOpen, setIsLeaseSigningDialogOpen] =
    useState(false);
  const [cancellingRequestId, setCancellingRequestId] = useState<string | null>(
    null
  );
  const [resolvingRequestId, setResolvingRequestId] = useState<string | null>(
    null
  );
  const [viewingRequest, setViewingRequest] =
    useState<MaintenanceRequest | null>(null);

  // Tab state with URL param support
  const [activeTab, setActiveTab] = useState<string>("overview");

  // Placeholder data for payments and documents (until implemented)
  const [payments] = useState<Payment[]>([]);
  const [documents] = useState<Document[]>([]);

  // Handle tab from URL params (for notification navigation)
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (
      tab &&
      ["overview", "lease", "payments", "maintenance", "documents"].includes(
        tab
      )
    ) {
      setActiveTab(tab);
      // Clear the param after setting tab
      searchParams.delete("tab");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Load dashboard data
  useEffect(() => {
    if (user?.id) {
      fetchDashboardData();
    }
  }, [user]);

  // Real-time subscription for maintenance requests
  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = subscribeToMaintenanceRequests(
      (requests) => {
        setMaintenanceRequests(requests);
      },
      (error) => {
        console.error("Error in maintenance subscription:", error);
      },
      {
        userId: user.id,
        userRole: "tenant",
      }
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user?.id]);

  const fetchDashboardData = async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      console.log("Fetching dashboard data for tenant:", user.id);
      const data = await getTenantDashboardData(user.id);
      console.log("Dashboard data received:", data);

      // Log lease ID for debugging
      if (data.lease) {
        console.log("📄 Tenant Lease ID:", data.lease.id);
        console.log("👤 Lease Tenant ID:", data.lease.tenantId);
        console.log("🏠 Lease Landlord ID:", data.lease.landlordId);
      }

      setLease(data.lease);
      setProperty(data.property);
      // Don't set maintenance requests here - they're managed by subscription
      setStats(data.stats);
    } catch (error) {
      console.error("Error fetching tenant dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setIsLoading(false);
    }
  };

  // Tenant action handlers
  const [deletingRequestId, setDeletingRequestId] = useState<string | null>(
    null
  );

  const handleDeleteRequest = async (requestId: string) => {
    try {
      await deleteMaintenanceRequest(requestId);
      toast.success("Maintenance request deleted");
      setDeletingRequestId(null);
      // No need to refetch - real-time subscription will update
    } catch (error: any) {
      toast.error(error.message || "Failed to delete request");
    }
  };

  const handleCancelRequest = async (requestId: string) => {
    try {
      await updateMaintenanceStatus(requestId, "cancelled", {
        notes: "Cancelled by tenant",
      });
      toast.success("Maintenance request cancelled");
      setCancellingRequestId(null);
      // No need to refetch - real-time subscription will update
    } catch (error: any) {
      toast.error(error.message || "Failed to cancel request");
    }
  };

  const handleMarkResolved = async (requestId: string) => {
    try {
      await updateMaintenanceStatus(requestId, "completed", {
        completedDate: new Date().toISOString(),
        notes: "Marked as resolved by tenant",
      });
      toast.success("Maintenance request marked as resolved");
      setResolvingRequestId(null);
      // No need to refetch - real-time subscription will update
    } catch (error: any) {
      toast.error(error.message || "Failed to update request");
    }
  };

  // Helper functions
  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "high":
        return <Badge className="bg-red-500">High</Badge>;
      case "medium":
        return <Badge className="bg-orange-500">Medium</Badge>;
      case "low":
        return <Badge className="bg-green-500">Low</Badge>;
      default:
        return <Badge variant="outline">{priority}</Badge>;
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-NG", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // No active lease state
  if (!lease || !property) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Welcome to Homespaces</CardTitle>
            <CardDescription>
              You don't have an active lease at the moment
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Active Lease</h3>
              <p className="text-muted-foreground max-w-md">
                You currently don't have an active lease agreement. Please
                contact your landlord if you believe this is an error.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const daysUntilPayment = getDaysUntilNextPayment(lease);

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <DashboardHeader
        title={`Welcome back, ${user?.name || "Tenant"}`}
        subtitle={`${property.title} - ${property.address}`}
        actionButton={{
          label: "New Maintenance Request",
          icon: <Plus className="mr-2 h-4 w-4" />,
          onClick: () => setIsMaintenanceDialogOpen(true),
        }}
      />

      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Rent Payment Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Next Rent Payment
            </CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNaira(lease.monthlyRent)}
            </div>
            <p className="text-xs text-muted-foreground">
              {daysUntilPayment === 0
                ? "Payment overdue"
                : daysUntilPayment === 1
                ? "Due tomorrow"
                : `Due in ${daysUntilPayment} days`}
            </p>
            <Progress
              value={Math.max(
                0,
                Math.min(100, 100 - (daysUntilPayment / 30) * 100)
              )}
              className="mt-3"
            />
          </CardContent>
        </Card>

        {/* Lease Status Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Lease Status</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="mb-3">
              <LeaseStatusBadge status={lease.status} />
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              Ends {formatDate(lease.endDate)}
            </p>
            {lease.status === "pending_signatures" && (
              <div className="space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Landlord:</span>
                  <Badge
                    variant={lease.landlordSigned ? "success" : "warning"}
                    className="text-xs"
                  >
                    {lease.landlordSigned ? "Signed" : "Pending"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Tenant:</span>
                  <Badge
                    variant={lease.tenantSigned ? "success" : "warning"}
                    className="text-xs"
                  >
                    {lease.tenantSigned ? "Signed" : "Pending"}
                  </Badge>
                </div>
                {!lease.tenantSigned && (
                  <Button
                    size="sm"
                    className="w-full mt-2"
                    onClick={() => setIsLeaseSigningDialogOpen(true)}
                  >
                    Sign Lease
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Maintenance Requests Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Maintenance</CardTitle>
            <Tool className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pendingRequests}</div>
            <p className="text-xs text-muted-foreground">
              {stats.totalRequests} total requests
            </p>
          </CardContent>
        </Card>

        {/* Property Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Property Type</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold capitalize">{property.type}</div>
            <p className="text-xs text-muted-foreground">
              {property.bedrooms} bed, {property.bathrooms} bath
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="lease">Lease</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
          <TabsTrigger value="maintenance">Maintenance</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>Common tasks and actions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => setIsMaintenanceDialogOpen(true)}
                >
                  <Tool className="mr-2 h-4 w-4" />
                  Submit Maintenance Request
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  disabled
                >
                  <CreditCard className="mr-2 h-4 w-4" />
                  Make Payment (Coming Soon)
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  disabled
                >
                  <FileText className="mr-2 h-4 w-4" />
                  View Lease Agreement
                </Button>
              </CardContent>
            </Card>

            {/* Recent Maintenance */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Maintenance</CardTitle>
                <CardDescription>
                  Your latest maintenance requests
                </CardDescription>
              </CardHeader>
              <CardContent>
                {maintenanceRequests.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No maintenance requests yet
                  </p>
                ) : (
                  <div className="space-y-3">
                    {maintenanceRequests.slice(0, 3).map((request) => (
                      <div
                        key={request.id}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">
                            {request.title}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(request.dateSubmitted)}
                          </p>
                        </div>
                        <MaintenanceStatusBadge status={request.status} />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Lease Tab */}
        <TabsContent value="lease">
          <LeaseDetailsCard
            lease={lease}
            propertyTitle={property.title}
            showSignButton={true}
            onSignClick={() => setIsLeaseSigningDialogOpen(true)}
            currentUserId={user?.id}
          />
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <PaymentHistoryTable
            payments={payments}
            emptyMessage="No payment history available. Payment system coming soon."
          />
        </TabsContent>

        {/* Maintenance Tab */}
        <TabsContent value="maintenance">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Maintenance Requests</CardTitle>
                  <CardDescription>
                    View and manage your maintenance requests
                  </CardDescription>
                </div>
                <Button onClick={() => setIsMaintenanceDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  New Request
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {maintenanceRequests.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No maintenance requests yet. Click "New Request" to submit
                  one.
                </div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Issue</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {maintenanceRequests.map((request) => {
                        const actions = getTenantActions(request.status);
                        return (
                          <TableRow key={request.id}>
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {request.title}
                                </span>
                                <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                                  {request.description}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="capitalize">
                              {request.category}
                            </TableCell>
                            <TableCell>
                              {getPriorityBadge(request.priority)}
                            </TableCell>
                            <TableCell>
                              <MaintenanceStatusBadge status={request.status} />
                            </TableCell>
                            <TableCell>
                              {formatDate(request.dateSubmitted)}
                            </TableCell>
                            <TableCell>
                              <TooltipProvider>
                                <div className="flex items-center justify-end gap-1">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() =>
                                          setViewingRequest(request)
                                        }
                                      >
                                        <Eye className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>View details</p>
                                    </TooltipContent>
                                  </Tooltip>

                                  {actions.canCancel && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={() =>
                                            setCancellingRequestId(request.id)
                                          }
                                          className="text-destructive hover:bg-destructive/10"
                                        >
                                          <XCircle className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Cancel request</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  )}

                                  {actions.canDelete && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={() =>
                                            setDeletingRequestId(request.id)
                                          }
                                          className="text-destructive hover:bg-destructive/10"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Delete request</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  )}

                                  {actions.canMarkResolved && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={() =>
                                            setResolvingRequestId(request.id)
                                          }
                                          className="text-green-600 hover:bg-green-50"
                                        >
                                          <CheckCircle className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Mark as resolved</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                </div>
                              </TooltipProvider>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <DocumentList
            documents={documents}
            emptyMessage="No documents available. Document system coming soon."
          />
        </TabsContent>
      </Tabs>

      {/* View Request Details Dialog */}
      <MaintenanceDetailsDialog
        request={viewingRequest}
        open={!!viewingRequest}
        onOpenChange={(open) => !open && setViewingRequest(null)}
      />

      {/* Maintenance Request Dialog */}
      <MaintenanceRequestDialog
        open={isMaintenanceDialogOpen}
        onOpenChange={setIsMaintenanceDialogOpen}
        onSuccess={() => {
          // Real-time subscription will update, just close dialog
          setIsMaintenanceDialogOpen(false);
        }}
        properties={property ? [property] : []}
        userId={user?.id || ""}
        prefilledData={{
          propertyId: property.id,
          unitId: lease.unitId,
          tenantId: user?.id,
        }}
        mode="tenant"
      />

      {/* Lease Signing Dialog */}
      <LeaseSigningDialog
        lease={lease}
        open={isLeaseSigningDialogOpen}
        onOpenChange={setIsLeaseSigningDialogOpen}
        onSuccess={fetchDashboardData}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deletingRequestId}
        onOpenChange={(open) => !open && setDeletingRequestId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Maintenance Request?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove your maintenance request. Since the
              landlord hasn't scheduled this yet, it's safe to delete. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Request</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deletingRequestId && handleDeleteRequest(deletingRequestId)
              }
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog
        open={!!cancellingRequestId}
        onOpenChange={(open) => !open && setCancellingRequestId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Maintenance Request?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel your maintenance request and notify the landlord.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Request</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                cancellingRequestId && handleCancelRequest(cancellingRequestId)
              }
              className="bg-yellow-600 text-white hover:bg-yellow-700"
            >
              Cancel Request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mark Resolved Confirmation Dialog */}
      <AlertDialog
        open={!!resolvingRequestId}
        onOpenChange={(open) => !open && setResolvingRequestId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark as Resolved?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark the maintenance request as completed. Please
              confirm that the repair work has been done to your satisfaction.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not Yet</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                resolvingRequestId && handleMarkResolved(resolvingRequestId)
              }
              className="bg-green-600 text-white hover:bg-green-700"
            >
              Confirm Resolved
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
